import { E3Charge } from '@e3dc-react/icons'
import { Box, keyframes, useTheme } from '@mui/material'
import React from 'react'
import { makeStyles } from 'tss-react/mui'

const move = keyframes`
      0% { background-position: 0 0 }
      100% { background-position: 50px 50px }
    `

interface StyleProps {
  discharging: boolean
}

const TRANSPARENCY_STRIPES = 50
const useStyles = makeStyles<StyleProps>()((theme, props) => {
  const { discharging } = props
  const backgroundColor = discharging ? theme.palette.wallboxWidget.discharging : theme.palette.wallboxWidget.charging

  const stripesColor = discharging
    ? `${theme.palette.wallboxWidget.dischargingStripes}${TRANSPARENCY_STRIPES}`
    : `${theme.palette.wallboxWidget.chargingStripes}${TRANSPARENCY_STRIPES}`

  return {
    animate: {
      backgroundColor,
      backgroundImage: `linear-gradient(-45deg, ${stripesColor} 25%, transparent 25%, transparent 50%, ${stripesColor} 50%, ${stripesColor} 75%, transparent 75%, transparent)`,
      animation: `${move} 3s linear infinite`,
      animationDirection: props.discharging ? 'reverse' : 'normal',
      backgroundSize: '50px 50px',
      transition: 'width 0.3s ease-in-out',
    },
    stopped: { backgroundColor: theme.palette.primary.main },
    chargingError: { backgroundColor: theme.palette.error.main },
    disabled: {
      backgroundColor: theme.palette.shades.shade03,
    },
  }
})

export interface ChargingProgressBarProps {
  chargeToInPercentage?: number
  currentChargeInPercentage?: number
  discharging?: boolean
  showChargeIcon?: boolean
  disabled?: boolean
  stopped?: boolean
  markerInPercentage?: number
  hasChargingError?: boolean
}
/**
 * Progress bar with charging, discharging, disabled and stopped states
 */
const ChargingProgressBar: React.FC<ChargingProgressBarProps> = ({
  chargeToInPercentage,
  discharging = false,
  currentChargeInPercentage,
  showChargeIcon = false,
  disabled = false,
  stopped = false,
  hasChargingError = false,
  markerInPercentage,
}) => {
  const theme = useTheme()
  const { classes } = useStyles({ discharging })

  let progressBarClassName = classes.animate

  if (disabled) {
    progressBarClassName = classes.disabled
  } else if (hasChargingError) {
    progressBarClassName = classes.chargingError
  } else if (stopped) {
    progressBarClassName = classes.stopped
  }
  return (
    <Box
      sx={{
        borderRadius: `${theme.shape.customBorderRadius.progressBar}px`,
        width: '100%',
        backgroundColor: theme.palette.grey.grey07,

        height: '36px',
      }}
    >
      <Box
        sx={{
          position: 'relative',
          left: 0,
          borderRadius: `${theme.shape.customBorderRadius.progressBar}px`,
          width: discharging || disabled ? 0 : `${Math.min(chargeToInPercentage ?? 0, 100)}%`,
          backgroundColor: theme.palette.primary.light,
          height: '100%',
          top: 0,
          transition: 'width 0.3s ease-in-out',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        {showChargeIcon && !discharging && <E3Charge color="primary" sx={{ position: 'relative', right: '-12px' }} />}
      </Box>
      <Box
        sx={{
          height: '100%',
          position: 'relative',
          left: 0,
          top: -36,
          borderRadius: `${theme.shape.customBorderRadius.progressBar}px`,
          width: `${Math.min(currentChargeInPercentage ?? 0, 100)}%`,
        }}
        className={progressBarClassName}
      />
      <Box
        sx={{
          height: '100%',
          position: 'relative',
          left: `${markerInPercentage}%`,
          width: markerInPercentage ? '3px' : 0,
          top: -72,
          backgroundColor: (markerInPercentage ?? 0) < (currentChargeInPercentage ?? 0) ? theme.palette.common.white : theme.palette.shades.shade03,
        }}
      />
    </Box>
  )
}

export default ChargingProgressBar
